import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import { Upload, FileText, RefreshCw, AlertCircle, Check } from 'lucide-react';
import toast from 'react-hot-toast';

interface SpreadsheetImportProps {
  onEmailsImported: (emails: string[]) => void;
}

export default function SpreadsheetImport({ onEmailsImported }: SpreadsheetImportProps) {
  const [loading, setLoading] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setValidationMessage(null);
    setFileError(null);
    setIsSuccess(false);

    // Check file type
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setFileError('Please select a CSV or Excel file');
      setLoading(false);
      return;
    }

    // Parse CSV file
    if (file.name.endsWith('.csv')) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          handleParsedData(results.data);
        },
        error: (error) => {
          setFileError(`Error parsing file: ${error.message}`);
          setLoading(false);
        }
      });
    } else {
      // For Excel files, we need to handle differently
      // This is a simplified version - you might want to use a library like xlsx
      // Since we're using .csv files from Google Sheets, we'll focus on CSV handling
      setFileError('Excel format is not fully supported yet. Please export as CSV from Google Sheets.');
      setLoading(false);
    }
  };

  const handleParsedData = (data: any[]) => {
    try {
      // Find email column by looking for common headers
      const emailColumnNames = ['email', 'email address', 'e-mail', 'emailaddress', 'emails'];
      let emailColumn = '';
      
      if (data.length === 0) {
        setFileError('No data found in the file');
        setLoading(false);
        return;
      }
      
      // Get the first row to detect headers
      const firstRow = data[0];
      const headers = Object.keys(firstRow);
      
      // Find the email column
      for (const header of headers) {
        if (emailColumnNames.includes(header.toLowerCase())) {
          emailColumn = header;
          break;
        }
      }
      
      // If we couldn't find an email column, try to detect it
      if (!emailColumn) {
        // Look for a column where values contain @ symbol
        for (const header of headers) {
          const sampleValue = String(firstRow[header]);
          if (sampleValue && sampleValue.includes('@')) {
            emailColumn = header;
            break;
          }
        }
      }
      
      if (!emailColumn) {
        setFileError('Could not find an email column in the file. Please ensure your file has an "email" column.');
        setLoading(false);
        return;
      }
      
      // Extract emails from the data
      const emails = data
        .filter(row => row[emailColumn] && String(row[emailColumn]).includes('@'))
        .map(row => String(row[emailColumn]).trim())
        .filter(email => {
          // Basic email validation
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return emailRegex.test(email);
        });
      
      if (emails.length === 0) {
        setFileError('No valid email addresses found in the file');
        setLoading(false);
        return;
      }
      
      // Remove duplicates
      const uniqueEmails = [...new Set(emails)];
      
      setImportedCount(uniqueEmails.length);
      setIsSuccess(true);
      setValidationMessage(`Successfully imported ${uniqueEmails.length} email addresses`);
      onEmailsImported(uniqueEmails);
      
      // Reset the file input for next import
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error processing data:', error);
      setFileError('Error processing the file data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFromGoogleSheet = async () => {
    setLoading(true);
    setValidationMessage(null);
    setFileError(null);
    setIsSuccess(false);
    
    try {
      // This is a URL to the Google Sheet shared by the user
      const sheetUrl = 'https://docs.google.com/spreadsheets/d/1P_0NT0uPZ3nSG8gMowlqTEA5CkZed350Z1qIJSHEt24/edit?usp=sharing';
      
      // Extract the sheet ID
      const sheetId = sheetUrl.split('/d/')[1].split('/')[0];
      
      // Create export URL for CSV
      const exportUrl = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv`;
      
      toast.success('Connecting to Google Sheet...');
      
      // Fetch the CSV data
      const response = await fetch(exportUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch sheet: ${response.statusText}`);
      }
      
      const csvText = await response.text();
      
      // Parse the CSV data
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          handleParsedData(results.data);
          toast.success('Google Sheet data imported successfully!');
        },
        error: (error) => {
          setFileError(`Error parsing Google Sheet: ${error.message}`);
          setLoading(false);
        }
      });
      
    } catch (error) {
      console.error('Error fetching Google Sheet:', error);
      setFileError('Failed to fetch data from Google Sheet. Please check the URL and try again.');
      toast.error('Failed to import from Google Sheet. Please try uploading a CSV file instead.');
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h3 className="text-lg font-semibold mb-4 flex items-center">
        <FileText className="h-5 w-5 mr-2 text-blue-500" />
        Import Email List
      </h3>
      
      <div className="space-y-4">
        <div className="flex flex-col space-y-2">
          <label className="text-sm font-medium text-gray-700">Upload CSV File</label>
          <div className="flex items-center gap-4">
            <div className="border-2 border-dashed rounded-md p-4 text-center flex-1">
              <Upload className="mx-auto h-10 w-10 text-gray-400" />
              <input
                type="file"
                onChange={handleFileChange}
                accept=".csv,.xlsx,.xls"
                className="hidden"
                id="file-upload"
                ref={fileInputRef}
              />
              <label
                htmlFor="file-upload"
                className="mt-2 cursor-pointer inline-block text-sm text-blue-500 hover:text-blue-600"
              >
                Select CSV File
              </label>
              <p className="text-xs text-gray-500 mt-1">
                CSV format with email column
              </p>
            </div>
            
            <div className="text-center">
              <p className="text-sm font-medium mb-2">OR</p>
            </div>
            
            <button
              onClick={fetchFromGoogleSheet}
              disabled={loading}
              className="bg-green-500 text-white py-2 px-4 rounded-md hover:bg-green-600 flex items-center gap-2 flex-1"
            >
              {loading ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              Import from Google Sheet
            </button>
          </div>
        </div>
        
        {fileError && (
          <div className="bg-red-50 p-3 rounded-md flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-700">{fileError}</p>
          </div>
        )}
        
        {isSuccess && (
          <div className="bg-green-50 p-3 rounded-md flex items-start">
            <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-700">Import successful!</p>
              <p className="text-sm text-green-600">{importedCount} email addresses imported</p>
            </div>
          </div>
        )}
        
        {validationMessage && !fileError && !isSuccess && (
          <div className="bg-blue-50 p-3 rounded-md">
            <p className="text-sm text-blue-700">{validationMessage}</p>
          </div>
        )}
        
        <div className="text-xs text-gray-500">
          <p>Supported formats: CSV files with an email column</p>
          <p>Note: Using Google Sheet requires the sheet to be accessible via the shared link</p>
        </div>
      </div>
    </div>
  );
}